// Chat-Dock der People-App (UX §2.6): Rahmen von @bar/ui, Inhalt aus der Registry (`dockPanels`).
// Unter md gibt es kein Dock: jeder Aufruf führt auf die Chat-Route (DESIGN §3.1.10).
// `?dock=c/<id>` öffnet das Panel „chat“ mit `{ route: 'c/<id>' }`; ⌘J schaltet es um.
import { DockPanel, useIsDesktop } from '@bar/ui/basis';
import { useDock, useDockPanels } from '@web/registry';
import { createElement, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

export const CHAT_PANEL = 'chat';

export function useChatDockShortcut(enabled: boolean) {
  const dock = useDock();
  const toggle = dock.toggle;
  useEffect(() => {
    if (!enabled) return;
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'j') {
        event.preventDefault();
        toggle(CHAT_PANEL);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, toggle]);
}

/** Rendert das offene Panel (Desktop) bzw. leitet mobil auf /chat um. */
export function ShellDock() {
  const dock = useDock();
  const panels = useDockPanels();
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const fromUrl = params.get('dock');
  const { open, close } = dock;

  // ?dock=… aus Links (Benachrichtigungen) übernehmen und aus der URL entfernen.
  useEffect(() => {
    if (!fromUrl) return;
    const next = new URLSearchParams(params);
    next.delete('dock');
    setParams(next, { replace: true });
    open(CHAT_PANEL, { route: fromUrl });
  }, [fromUrl, params, setParams, open]);

  const onChatPage = location.pathname === '/chat' || location.pathname.startsWith('/chat/');
  const panel = panels.find((p) => p.key === dock.panel);

  // Mobil bzw. auf /chat selbst: kein Dock, sondern die Chat-Seite.
  useEffect(() => {
    if (!dock.panel || (isDesktop && !onChatPage)) return;
    const route = dock.params.route;
    close();
    if (!onChatPage || route) void navigate(route ? `/chat/${route}` : '/chat');
  }, [dock.panel, dock.params, isDesktop, onChatPage, close, navigate]);

  const visible = Boolean(panel) && isDesktop && !onChatPage;
  return (
    <DockPanel open={visible} onClose={close} label={panel?.label ?? 'Chat'}>
      {panel ? createElement(panel.Component, { params: dock.params, close }) : null}
    </DockPanel>
  );
}
